import { getQuotes } from "@stryker/core/api/quotes";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EmptyState from "@/components/EmptyState";
import QuoteCard from "@/components/quotes/QuoteCard";

type MobileQuotesListProps = {
  search: string;
};

export default function MobileQuotesList({ search }: MobileQuotesListProps) {
  const loadMoreRef = useRef(null);
  const navigate = useNavigate();

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QueryKeys.QuotesList, search],
    queryFn: async ({ pageParam = 1 }) => {
      return await getQuotes({ jobNumber: search, page: pageParam });
    },
    getNextPageParam: (lastPage) => {
      const current = Number(lastPage.Pagination.currentPage);
      return current === lastPage.Pagination.totalPages ? undefined : current + 1;
    },
    select: (data) => {
      return {
        ...data,
        pages: data.pages.map((response) => ({
          ...response,
          Pagination: {
            ...response.Pagination,
            currentPage: Number.parseInt(response.Pagination.currentPage, 10),
          },
        })),
      };
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    if (!hasNextPage) return; // If there's no next page, don't even set up the observer

    const observer = new IntersectionObserver((entries) => {
      // Check if the entry is intersecting and trigger fetchNextPage
      if (entries[0].isIntersecting) {
        void fetchNextPage();
      }
    });

    // Start observing the ref
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    // Clean up the observer when the component is unmounted
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, fetchNextPage]);

  return (
    <>
      <div className="mb-11 flex flex-col gap-4">
        {data?.pages
          .flatMap((page) => page.results)
          .map((quote) => (
            <QuoteCard quote={quote} key={quote.quote?.id} />
          ))}
        {data?.pages[0]?.Pagination.count === 0 && (
          <EmptyState
            buttonText="Create custom Quote"
            onSubmit={() => {
              navigate("/quotes/create-edit-custom-quote");
            }}
          />
        )}
        <div ref={loadMoreRef} />
      </div>
    </>
  );
}
